/**
 * DroneBay — modular hangar deployment cycle for subordinate mining drones,
 * targeting the (orbiting) planets.
 *
 * Flight: hangar ─LAUNCH (straight out the hull's front ~1s)─▶ MOVING_TO_SPOT
 * (smooth curved turn to a random firing offset around the planet) ─arrive─▶
 * ATTACKING (lock a random point on the planet, mine + beam) ─relocate─▶ …
 * full/deselected ─▶ RETURNING ─dock─▶ deposit ─▶ relaunch (loop) or despawn.
 *
 *  • Retarget — one selected planet at a time; up to `maxConcurrent` drones total
 *    work it. Selecting a different planet retargets the SAME drones (they fly
 *    straight over), never spawning extras.
 *  • Moving targets — firing/attack points are stored as OFFSETS relative to the
 *    planet centre and resolved from the planet's live position each tick, so
 *    drones follow the planet as it orbits.
 */
import { Vector2 } from "@/math/Vector2";
import { MINING_DRONE } from "@/config/balance";
import type { MineableBody } from "@/game/world/body";

export type DroneUnitState =
  | "launching"
  | "movingToSpot"
  | "attacking"
  | "returning";

export interface DroneUnit {
  id: number;
  pos: Vector2;
  vel: Vector2;
  heading: number;
  cargo: number;
  frame: number; // 0..1 seed (unused once a skin is chosen)
  state: DroneUnitState;
  targetId: string;
  firingOffset: Vector2; // firing position relative to the planet centre
  attackOffset: Vector2; // attack point relative to the planet centre
  firingPos: Vector2; // scratch: resolved world firing position
  attackPoint: Vector2; // scratch: resolved world attack point
  spotTimer: number;
  launchTimer: number;
  beamTarget: Vector2 | null;
  floatAccum: number; // batches mined minerals into periodic tick numbers
  floatTimer: number;
  moveTime: number; // time spent trying to reach the firing spot (circle detector)
  resetTimer: number; // >0 → dashing straight to break an orbit
}

export interface DroneBayCtx {
  hangar: Vector2;
  hangarFacing: Vector2; // ship front unit vector — drones launch along this
  gateLeft?: Vector2; // Mk III side gates — drones launch outward from these
  gateRight?: Vector2;
  candidates: MineableBody[]; // ore bodies inside the deploy circle
  hullTargets: Set<string>; // rocks the hull is firing on (drones prefer others)
  maxConcurrent: number;
  droneSpeed: number; // effective (upgraded) drone stats
  droneCargo: number;
  droneRate: number;
  bodyById: (id: string) => MineableBody | null;
  deposit: (amount: number) => void;
  emitFloater: (x: number, y: number, amount: number) => void;
}

export class DroneBay {
  readonly units: DroneUnit[] = [];
  private nextId = 1;
  private spawnTimer = 0;
  // Current effective stats, refreshed from ctx each frame (upgrade-aware).
  private curSpeed: number = MINING_DRONE.speed;
  private curCargo: number = MINING_DRONE.cargoCapacity;
  private curRate: number = MINING_DRONE.mineRatePerSec;

  update(dt: number, ctx: DroneBayCtx): void {
    this.curSpeed = ctx.droneSpeed;
    this.curCargo = ctx.droneCargo;
    this.curRate = ctx.droneRate;
    // Deploy up to `maxConcurrent` drones as long as rocks are inside the circle;
    // each new drone is assigned to the least-crowded rock (so the swarm splits).
    this.spawnTimer -= dt;
    if (
      ctx.candidates.length > 0 &&
      this.spawnTimer <= 0 &&
      this.units.length < ctx.maxConcurrent
    ) {
      // Two blue side gates → launch one drone from EACH gate simultaneously (2 out
      // per cycle); otherwise a single drone leaves along the hull's front.
      if (ctx.gateLeft && ctx.gateRight) {
        this.spawnDrone(ctx, ctx.gateLeft);
        if (this.units.length < ctx.maxConcurrent) this.spawnDrone(ctx, ctx.gateRight);
      } else {
        this.spawnDrone(ctx);
      }
      this.spawnTimer = MINING_DRONE.spawnInterval;
    }

    for (let i = this.units.length - 1; i >= 0; i--) {
      const u = this.units[i]!;
      if (!this.updateUnit(u, dt, ctx)) this.units.splice(i, 1);
    }
  }

  /** Create + launch one drone, from a side gate if given, else the hull front. */
  private spawnDrone(ctx: DroneBayCtx, gate?: Vector2): void {
    let lpx = ctx.hangar.x;
    let lpy = ctx.hangar.y;
    let ldx = ctx.hangarFacing.x;
    let ldy = ctx.hangarFacing.y;
    if (gate) {
      lpx = gate.x;
      lpy = gate.y;
      const ox = gate.x - ctx.hangar.x;
      const oy = gate.y - ctx.hangar.y;
      const len = Math.hypot(ox, oy) || 1;
      ldx = ox / len;
      ldy = oy / len;
    }
    const u: DroneUnit = {
      id: this.nextId++,
      pos: new Vector2(lpx, lpy),
      vel: new Vector2(ldx * this.curSpeed, ldy * this.curSpeed),
      heading: Math.atan2(ldy, ldx),
      cargo: 0,
      frame: Math.random(),
      state: "launching",
      targetId: "",
      firingOffset: new Vector2(),
      attackOffset: new Vector2(),
      firingPos: new Vector2(),
      attackPoint: new Vector2(),
      spotTimer: 0,
      launchTimer: this.launchTime(),
      beamTarget: null,
      floatAccum: 0,
      floatTimer: 0,
      moveTime: 0,
      resetTimer: 0,
    };
    this.assignTarget(u, ctx);
    this.units.push(u);
  }

  private updateUnit(u: DroneUnit, dt: number, ctx: DroneBayCtx): boolean {
    // Decide the objective before moving (skip if already heading home):
    if (u.state !== "returning") {
      if (u.cargo >= this.curCargo) {
        u.state = "returning";
        u.beamTarget = null;
      } else {
        const cur = ctx.bodyById(u.targetId);
        if (!cur || cur.ore <= 0) {
          // This rock is finished/gone → commit to another rock in range, or dock.
          if (this.assignTarget(u, ctx)) {
            if (u.state === "attacking") {
              u.state = "movingToSpot";
              u.moveTime = 0;
              u.resetTimer = 0;
            }
            u.beamTarget = null;
          } else {
            u.state = "returning";
            u.beamTarget = null;
          }
        }
      }
    }

    const planet = ctx.bodyById(u.targetId);
    if (u.state !== "returning" && (!planet || planet.ore <= 0)) {
      u.state = "returning";
      u.beamTarget = null;
    }

    switch (u.state) {
      case "launching": {
        u.beamTarget = null;
        u.launchTimer -= dt;
        u.pos.addSelf(u.vel.scale(dt));
        u.heading = Math.atan2(u.vel.y, u.vel.x);
        if (planet && planet.pos.distanceTo(u.pos) <= MINING_DRONE.spotDistMax) {
          // reached the target area during takeoff → mine right away from here
          u.firingOffset.set(u.pos.x - planet.pos.x, u.pos.y - planet.pos.y);
          u.state = "attacking";
          u.spotTimer = MINING_DRONE.attackHold;
        } else if (u.launchTimer <= 0) {
          u.state = "movingToSpot";
          u.moveTime = 0;
          u.resetTimer = 0;
        }
        break;
      }

      case "movingToSpot": {
        u.beamTarget = null;
        if (!planet) break;
        // firing position rides with the (orbiting) planet
        u.firingPos.set(
          planet.pos.x + u.firingOffset.x,
          planet.pos.y + u.firingOffset.y,
        );
        if (u.resetTimer > 0) {
          // Orbit-break: dash straight forward for a moment, no steering.
          u.resetTimer -= dt;
          const c = Math.cos(u.heading) * this.curSpeed;
          const s = Math.sin(u.heading) * this.curSpeed;
          u.vel.set(c, s);
          u.pos.x += c * dt;
          u.pos.y += s * dt;
          break;
        }
        u.moveTime += dt;
        if (this.steerAndMove(u, u.firingPos, dt, MINING_DRONE.arriveRadius)) {
          u.state = "attacking";
          u.spotTimer = MINING_DRONE.attackHold;
          u.moveTime = 0;
        } else if (u.moveTime > 2) {
          // Circling too long → reset: dash forward and re-pick an approach angle.
          u.resetTimer = 0.6;
          u.moveTime = 0;
          this.pickSpot(u, planet);
        }
        break;
      }

      case "attacking": {
        if (!planet) {
          u.state = "returning";
          u.beamTarget = null;
          break;
        }
        // stay locked to the firing spot / attack point as the planet drifts
        u.firingPos.set(
          planet.pos.x + u.firingOffset.x,
          planet.pos.y + u.firingOffset.y,
        );
        u.attackPoint.set(
          planet.pos.x + u.attackOffset.x,
          planet.pos.y + u.attackOffset.y,
        );
        u.pos.copy(u.firingPos);
        u.vel.set(0, 0);
        u.heading = Math.atan2(
          u.attackPoint.y - u.pos.y,
          u.attackPoint.x - u.pos.x,
        );
        u.beamTarget = u.attackPoint;
        const got = Math.min(
          this.curRate * dt,
          planet.ore,
          this.curCargo - u.cargo,
        );
        planet.ore -= got;
        u.cargo += got;
        u.floatAccum += got;
        u.floatTimer += dt;
        if (u.floatTimer >= 0.3 && u.floatAccum > 0.5) {
          ctx.emitFloater(u.attackPoint.x, u.attackPoint.y, u.floatAccum);
          u.floatAccum = 0;
          u.floatTimer = 0;
        }
        u.spotTimer -= dt;
        if (u.spotTimer <= 0) {
          this.pickSpot(u, planet);
          u.state = "movingToSpot";
          u.moveTime = 0;
          u.resetTimer = 0;
          u.beamTarget = null;
        }
        break;
      }

      case "returning": {
        u.beamTarget = null;
        // Mk III: dock at (and relaunch from) the NEAREST side gate — the sides
        // are the hangar + runway, so drones fly in and out on the sides only.
        const gates = ctx.gateLeft && ctx.gateRight;
        const dock = gates ? this.nearestGate(u, ctx) : ctx.hangar;
        if (this.steerAndMove(u, dock, dt, MINING_DRONE.arriveRadius)) {
          ctx.deposit(u.cargo);
          u.cargo = 0;
          if (this.assignTarget(u, ctx)) {
            let dx = ctx.hangarFacing.x;
            let dy = ctx.hangarFacing.y;
            if (gates) {
              const ox = dock.x - ctx.hangar.x;
              const oy = dock.y - ctx.hangar.y;
              const len = Math.hypot(ox, oy) || 1;
              dx = ox / len;
              dy = oy / len;
            }
            u.vel.set(dx * this.curSpeed, dy * this.curSpeed);
            u.heading = Math.atan2(dy, dx);
            u.launchTimer = this.launchTime();
            u.state = "launching";
          } else {
            return false; // no rock left in range → dock & despawn
          }
        }
        break;
      }
    }
    return true;
  }

  /** The side gate (Mk III) nearest this drone — its dock/launch point. */
  private nearestGate(u: DroneUnit, ctx: DroneBayCtx): Vector2 {
    const gl = ctx.gateLeft!;
    const gr = ctx.gateRight!;
    const dl = (u.pos.x - gl.x) ** 2 + (u.pos.y - gl.y) ** 2;
    const dr = (u.pos.x - gr.x) ** 2 + (u.pos.y - gr.y) ** 2;
    return dl <= dr ? gl : gr;
  }

  /** Assign this drone to the least-crowded ore rock in the deploy circle,
   * preferring rocks the hull isn't firing on, then the nearest. Returns false
   * when there are no candidates. Commits the drone until that rock is gone. */
  private assignTarget(u: DroneUnit, ctx: DroneBayCtx): boolean {
    const cands = ctx.candidates;
    if (cands.length === 0) return false;
    const counts = new Map<string, number>();
    for (const other of this.units) {
      if (other === u || other.state === "returning") continue;
      counts.set(other.targetId, (counts.get(other.targetId) ?? 0) + 1);
    }
    let best: MineableBody | null = null;
    let bestScore = Infinity;
    for (const b of cands) {
      const cnt = counts.get(b.id) ?? 0;
      const hull = ctx.hullTargets.has(b.id) ? 1 : 0;
      const dist = Math.sqrt(b.pos.distanceSqTo(u.pos));
      const score = cnt * 1e6 + hull * 4e5 + dist; // spread first, avoid hull, then nearest
      if (score < bestScore) {
        bestScore = score;
        best = b;
      }
    }
    if (!best) return false;
    u.targetId = best.id;
    this.pickSpot(u, best);
    return true;
  }

  /** Roll a fresh firing offset around the body and a fresh attack offset on it. */
  private pickSpot(u: DroneUnit, planet: MineableBody): void {
    const ang = Math.random() * Math.PI * 2;
    const dist =
      MINING_DRONE.spotDistMin +
      Math.random() * (MINING_DRONE.spotDistMax - MINING_DRONE.spotDistMin);
    u.firingOffset.set(Math.cos(ang) * dist, Math.sin(ang) * dist);
    const t = Math.random() * Math.PI * 2;
    const r =
      Math.sqrt(Math.random()) * planet.radius * MINING_DRONE.attackPointFrac;
    u.attackOffset.set(Math.cos(t) * r, Math.sin(t) * r);
  }

  /** Steer velocity toward `goal` with a capped turn rate (smooth arc). */
  private steerAndMove(
    u: DroneUnit,
    goal: Vector2,
    dt: number,
    stopDist: number,
  ): boolean {
    const desired = goal.sub(u.pos);
    const dist = desired.length();
    if (dist <= stopDist) {
      u.vel.set(0, 0);
      return true;
    }
    const desiredAngle = Math.atan2(desired.y, desired.x);
    const curAngle =
      u.vel.lengthSq() > 1e-6 ? Math.atan2(u.vel.y, u.vel.x) : u.heading;
    let diff = desiredAngle - curAngle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    const rate = dist < 60 ? MINING_DRONE.turnRate * 3 : MINING_DRONE.turnRate;
    const maxStep = rate * dt;
    const step = diff > maxStep ? maxStep : diff < -maxStep ? -maxStep : diff;
    const a = curAngle + step;
    u.vel.set(Math.cos(a) * this.curSpeed, Math.sin(a) * this.curSpeed);
    u.heading = a;
    u.pos.addSelf(u.vel.scale(dt));
    return false;
  }

  private launchTime(): number {
    return (
      MINING_DRONE.launchTimeMin +
      Math.random() * (MINING_DRONE.launchTimeMax - MINING_DRONE.launchTimeMin)
    );
  }

  clear(): void {
    this.units.length = 0;
    this.spawnTimer = 0;
  }
}
